import { useEffect, useMemo, useState } from 'react';
import MethodBody from './MethodBody';
import { effectiveMethod, startBatch } from '../../lib/labData';
import { groupByPhase } from '../../lib/formulaModel';
import { todayISO } from '../../lib/dates';
import {
  batchQuantities,
  containerCount,
  formatBatchG,
  formatGrams,
  formatPct,
  netFillG,
  plannedBatchG,
  resolveVariant,
} from '../../lib/lab';

const STEPS = ['What are you making', 'How many containers', 'Confirm the batch sheet'];

const blankRow = () => ({ label: '', grams: '', qty: '' });

/* Make a batch — three steps, one visible at a time.
 *
 * This is where the fill calculator belongs. It used to live inside the
 * formula editor, which meant working out how much to make involved opening
 * the formula for editing; scaling a batch must never touch the formula.
 */
export default function MakeBatchWizard({ formulas, methods, initialFormulaId, onClose, onStarted }) {
  const [step, setStep] = useState(1);
  const [formulaId, setFormulaId] = useState(initialFormulaId || '');
  const [variantId, setVariantId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [pickVersion, setPickVersion] = useState(false);
  const [rows, setRows] = useState([blankRow()]);
  const [overage, setOverage] = useState('');
  const [madeOn, setMadeOn] = useState(todayISO());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const formula = useMemo(() => formulas.find((f) => f.id === formulaId) || null, [formulas, formulaId]);
  const version = useMemo(() => {
    if (!formula) return null;
    return formula.versions.find((v) => v.id === versionId) || formula.currentVersion;
  }, [formula, versionId]);
  const variant = useMemo(
    () => (formula?.variants || []).find((v) => v.id === variantId) || null,
    [formula, variantId]
  );
  const method = useMemo(() => effectiveMethod(formula, methods), [formula, methods]);

  // Overage and container presets come from the product type: every lip balm
  // fills the same tube, so this is not per-formula knowledge.
  useEffect(() => {
    if (!formula) return;
    setOverage(String(formula.product_type?.default_overage_pct ?? 10));
    setRows([blankRow()]);
    setVariantId('');
    setVersionId('');
  }, [formula]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const presets = formula?.product_type?.container_presets || [];
  const containers = rows
    .filter((r) => r.label && Number(r.grams) > 0 && Number(r.qty) > 0)
    .map((r) => ({ label: r.label, grams: Number(r.grams), qty: Number(r.qty) }));

  const netFill = netFillG(containers);
  const planned = plannedBatchG(netFill, Number(overage) || 0);
  const units = containerCount(containers);

  const resolution = useMemo(() => {
    if (!version) return { lines: [], total: 0, error: null };
    return resolveVariant({
      lines: version.formula_ingredients || [],
      variant,
      flexIngredientId: version.flex_ingredient_id,
      ingredients: (version.formula_ingredients || []).map((l) => l.ingredient).filter(Boolean),
    });
  }, [version, variant]);

  const { rows: quantities } = batchQuantities(resolution.lines, planned);

  const variantsRequired = (formula?.activeVariants || []).length > 0;
  const canLeaveStep1 = Boolean(formula && version && (!variantsRequired || variantId));
  const canLeaveStep2 = containers.length > 0 && planned > 0;

  const handleStart = async (status) => {
    setSaving(true);
    setError(null);
    try {
      const batch = await startBatch({
        formulaId: version.id,
        variantId: variantId || null,
        madeOn,
        containers,
        netFill,
        overagePct: Number(overage) || 0,
        plannedG: planned,
        // Frozen here, on purpose: this batch must still show what it used
        // after the formula moves on.
        resolvedLines: quantities.map((line) => ({
          ingredient_id: line.ingredient_id || null,
          ingredient_name: line.ingredient_name,
          phase: line.phase || null,
          percentage: line.percentage,
          grams: line.gramsRounded,
        })),
        methodSnapshot: method
          ? { name: method.name, steps: method.steps || [], temperatures: method.temperatures || [] }
          : null,
        status,
      });
      onStarted(batch);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const visible = formulas.filter((f) => {
    if (f.status === 'Archived') return false;
    const needle = query.trim().toLowerCase();
    return !needle || f.name.toLowerCase().includes(needle);
  });

  return (
    <div className="asset-modal-backdrop" onClick={onClose}>
      <div
        className="version-modal wizard"
        role="dialog"
        aria-modal="true"
        aria-label="Make a batch"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="asset-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <header className="version-modal-head">
          <div>
            <h3>Make a batch</h3>
            <span className="formula-detail-type">
              Step {step} of 3 · {STEPS[step - 1]}
            </span>
          </div>
        </header>

        <ol className="wizard-steps">
          {STEPS.map((label, i) => (
            <li key={label} className={i + 1 === step ? 'active' : i + 1 < step ? 'done' : ''}>
              <span>{i + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        {/* ------------------------------------------------------- step 1 */}
        {step === 1 && (
          <div className="wizard-body">
            <input
              className="lab-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search formulas"
            />
            <ul className="wizard-picker">
              {visible.map((f) => (
                <li key={f.id}>
                  <button
                    className={'wizard-pick' + (f.id === formulaId ? ' selected' : '')}
                    onClick={() => setFormulaId(f.id)}
                  >
                    <span className="lab-row-name">{f.name}</span>
                    <span className="lab-row-meta">{f.product_type?.name}</span>
                    <span className="lab-row-meta">
                      {f.currentVersion ? `v${f.currentVersion.version_number}` : 'No version'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {formula && (
              <>
                {variantsRequired && (
                  <label className="wizard-field">
                    Variant
                    <select value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                      <option value="">Choose a variant…</option>
                      {formula.activeVariants.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <div className="wizard-version">
                  {pickVersion ? (
                    <label className="wizard-field">
                      Version
                      <select
                        value={versionId || formula.currentVersion?.id || ''}
                        onChange={(e) => setVersionId(e.target.value)}
                      >
                        {formula.versions.map((v) => (
                          <option key={v.id} value={v.id}>
                            v{v.version_number}
                            {v.is_current ? ' (current)' : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <p>
                      Using <strong>v{version?.version_number}</strong>{' '}
                      <button className="link-button" onClick={() => setPickVersion(true)}>
                        Use a different version
                      </button>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ------------------------------------------------------- step 2 */}
        {step === 2 && (
          <div className="wizard-body">
            {presets.length > 0 && (
              <div className="wizard-chips">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    className="wizard-chip"
                    onClick={() =>
                      setRows((r) => {
                        const empty = r.findIndex((row) => !row.label);
                        const next = { label: preset.label, grams: preset.grams, qty: '' };
                        if (empty >= 0) return r.map((row, i) => (i === empty ? next : row));
                        return [...r, next];
                      })
                    }
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}

            <div className="wizard-rows">
              {rows.map((row, i) => (
                <div className="wizard-row" key={i}>
                  <input
                    type="text"
                    value={row.label}
                    onChange={(e) =>
                      setRows((r) => r.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))
                    }
                    placeholder="Container"
                  />
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={row.grams}
                    onChange={(e) =>
                      setRows((r) => r.map((x, idx) => (idx === i ? { ...x, grams: e.target.value } : x)))
                    }
                    placeholder="g each"
                  />
                  <span className="temp-dash">×</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={row.qty}
                    onChange={(e) =>
                      setRows((r) => r.map((x, idx) => (idx === i ? { ...x, qty: e.target.value } : x)))
                    }
                    placeholder="qty"
                  />
                  {rows.length > 1 && (
                    <button
                      className="yield-remove"
                      onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))}
                      aria-label="Remove size"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button className="yield-add" onClick={() => setRows((r) => [...r, blankRow()])}>
                + Add another size
              </button>
            </div>

            <label className="wizard-field wizard-overage">
              Overage
              <span className="wizard-overage-input">
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={overage}
                  onChange={(e) => setOverage(e.target.value)}
                />
                %
              </span>
              <em>Covers what stays in the pot and on the spatula.</em>
            </label>

            <div className="wizard-summary">
              <div>
                <span>Net fill</span>
                <strong>{formatBatchG(netFill)}</strong>
                <em>
                  {units} container{units === 1 ? '' : 's'}
                </em>
              </div>
              <div>
                <span>Overage ({Number(overage) || 0}%)</span>
                <strong>{formatBatchG(planned - netFill)}</strong>
              </div>
              <div className="wizard-summary-total">
                <span>Batch size</span>
                <strong>{formatBatchG(planned)}</strong>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------- step 3 */}
        {step === 3 && (
          <div className="wizard-body">
            <div className="wizard-confirm-head">
              <div>
                <h4>
                  {formula.name}
                  {variant ? ` — ${variant.name}` : ''}
                </h4>
                <p className="lab-row-meta">
                  v{version.version_number} · {formatBatchG(planned)} · {units} containers
                </p>
              </div>
              <label className="wizard-field">
                Date
                <input type="date" value={madeOn} onChange={(e) => setMadeOn(e.target.value)} />
              </label>
            </div>

            {resolution.error ? (
              <p className="formula-error">{resolution.error.message}</p>
            ) : (
              <table className="lab-table">
                <thead>
                  <tr>
                    <th>Ingredient</th>
                    <th className="numeric">%</th>
                    <th className="numeric">Weight</th>
                  </tr>
                </thead>
                {groupByPhase(quantities).map(([phase, lines]) => (
                  <tbody key={phase}>
                    <tr className="phase-row">
                      <td colSpan={3}>{phase}</td>
                    </tr>
                    {lines.map((line, i) => (
                      <tr key={`${line.ingredient_name}-${i}`}>
                        <td>
                          {line.ingredient_name}
                          {line.flex && (
                            <span className="flex-tag">
                              <i className="flex-dot" /> flex
                            </span>
                          )}
                        </td>
                        <td className="numeric">{formatPct(line.percentage)}</td>
                        <td className="numeric strong">{formatGrams(line.gramsRounded)}</td>
                      </tr>
                    ))}
                  </tbody>
                ))}
                <tfoot>
                  <tr className="lab-total ok">
                    <td>Total</td>
                    <td className="numeric">{formatPct(resolution.total)}</td>
                    <td className="numeric">{formatBatchG(planned)}</td>
                  </tr>
                </tfoot>
              </table>
            )}

            <details className="wizard-method">
              <summary>{method ? `Method: ${method.name}` : 'No method attached'}</summary>
              {method ? (
                <MethodBody method={method} />
              ) : (
                <p className="formula-muted">
                  This batch sheet will have no steps. Attach a method on the formula page.
                </p>
              )}
            </details>
          </div>
        )}

        {error && <p className="formula-error">{error}</p>}

        <div className="wizard-actions">
          {step > 1 && (
            <button className="ghost-button" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          <span className="wizard-spacer" />
          {step < 3 ? (
            <button
              className="primary-action"
              disabled={step === 1 ? !canLeaveStep1 : !canLeaveStep2}
              onClick={() => setStep(step + 1)}
            >
              Next
            </button>
          ) : (
            <>
              <button
                className="ghost-button"
                disabled={saving || Boolean(resolution.error)}
                onClick={() => handleStart('planned')}
              >
                Save as planned
              </button>
              <button
                className="primary-action"
                disabled={saving || Boolean(resolution.error)}
                onClick={() => handleStart('in_progress')}
              >
                {saving ? 'Starting…' : 'Start batch'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
